import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import { withRouter } from "react-router-dom";
import styled from "styled-components/macro";
import CloseIcon from "@material-ui/icons/Close";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@material-ui/core";
import { SourceFileLoader, InputTypeAhead } from "@loanbossinc/component-library";

import { actionCreators as vendorIntegrationsActionCreators } from "../../components/Admin/VendorIntegrations/vendorIntegrations.store";
import { actionCreators as rentRollActionCreators } from "../../pages/propertyDetail/property-rent-rolls/rentRolls.store";

import { actionCreators as uploadRentRollActionCreators } from "./uploadRentRollModal.store";

const StyledDialogIconButton = styled(IconButton)`
  && {
    position: absolute;
    right: 13px;
    top: 8px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    color: #666;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  && {
    h2 {
      font-family: "Work Sans";
      font-size: 24px;
      font-weight: 500;
      line-height: 28px;
    }
  }
`;

const StyledSFLoader = styled(SourceFileLoader)`
  & .drop-zone-wrapper {
    pointer-events: ${props => props.disabledSelect && "none"};
    margin-top: 1em;
  }
`;

const SUPPORTED_FILE_TYPES = [".csv", ".xls", ".xlsx"];

export const UploadRentRollModal = props => {
  const {
    isOpen,
    uploadSuccessful,
    isUploading,
    onClose,
    propertyData,
    setRentRollUploadSelections,
    sourceId,
    title,
    uploadErrorMessage,
    upload,
    userId,
    getVendorIntegrationsForCompany,
    companyId,
    companyIntegrations,
    setRentRollsSelections
  } = props;

  const setSourceId = value => {
    if (value) {
      setRentRollUploadSelections({ sourceId: value });
    } else {
      setRentRollUploadSelections({ sourceId: null });
    }
  };

  const onCloseModal = () => {
    onClose();
    setRentRollUploadSelections({ sourceId: null, uploadErrorMessage: null, selectedFile: null, uploadSuccessful: false });
  };

  useEffect(() => {
    if (uploadSuccessful) {
      onCloseModal();
      setRentRollsSelections({ shouldRefetchRentRolls: true });
    }
  }, [uploadSuccessful]);

  useEffect(() => {
    if (!companyIntegrations && companyId) {
      getVendorIntegrationsForCompany({ companyId });
    }
  }, [companyIntegrations, companyId]);

  const handleUploadError = errors => {
    const error = errors[0].errors[0];

    let message;
    switch (error.code) {
      case "file-too-large":
        message = "File size has to be 1 Mb or less";
        break;
      case "file-invalid-type":
        message = "File type must be one of csv, xls, xlsx";
        break;
      default:
        message = "Unsupported File";
    }

    setRentRollUploadSelections({ uploadErrorMessage: message });
  };

  const sources = [{ id: "a1473f4a-0cfe-43b7-8c84-1fe3f3ed6736", name: "Yardi" },
    { id: "30c31266-8ca0-4a86-ba7e-e0242b37f5e6", name: "MRI" }];

  const renderIntegrationWarning = () => {
    const vendorName = sources.find(s => s.id === sourceId)?.name;

    return (
      <div style={{ color: "#FF1744", fontFamily: "Work Sans", fontSize: "14px", width: "100%" }}>{`Please enable ${vendorName} integration in settings`}</div>
    );
  };

  const isIntegrationEnabled = companyIntegrations && sourceId && Boolean(companyIntegrations.find(int => int.vendorId === sourceId));

  const uploadMessage = !uploadErrorMessage ? "Add a file by dragging it here, or" : uploadErrorMessage;
  const loaderTextContent = !isUploading ? uploadMessage : "Uploading ...";

  return (
    <Dialog open={isOpen} onClose={onCloseModal}>
      <StyledDialogTitle>{title}</StyledDialogTitle>
      <StyledDialogIconButton onClick={onCloseModal}>
        <CloseIcon id="closeIcon" style={{ fontSize: 22 }} />
      </StyledDialogIconButton>
      <StyledDialogContent>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
            <InputTypeAhead
              fullWidth
              variant="filled"
              id="sourceId"
              name="Vendor"
              label="Vendor"
              options={sources}
              displayWith={option => option && (option.name || "unknown")}
              setValueWith={option => option && option.id}
              onChange={setSourceId}
              value={sourceId}
            />
            <div style={{ height: "25px", display: "flex", alignItems: "center" }}>{!isIntegrationEnabled && sourceId && renderIntegrationWarning()}</div>
          </div>
          <StyledSFLoader
            content={loaderTextContent}
            multiple={false}
            onDrop={(files, error) => {
              if (!sourceId) {
                setRentRollUploadSelections({ uploadErrorMessage: "Please select a vendor first" });

                return;
              }

              if (error.length) {
                handleUploadError(error);

                return;
              }

              if (files.length) {
                upload(propertyData.property.dealId, propertyData.property.id, userId, sourceId, "rent-roll", files[0], "false", companyId);
              }
            }}
            disabledSelect={!isIntegrationEnabled}
            accept={SUPPORTED_FILE_TYPES}
            maxSize={100000000}
          />
        </div>
      </StyledDialogContent>
    </Dialog>
  );
};

const mapStateToProps = ({ uploadRentRollModal, vendorIntegrations }) => ({
  ...uploadRentRollModal,
  ...vendorIntegrations
});
const mapDispatchToProps = dispatch => bindActionCreators({ ...uploadRentRollActionCreators, ...vendorIntegrationsActionCreators, ...rentRollActionCreators }, dispatch);

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(UploadRentRollModal);
